import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Users, TrendingUp, Award, X, BookOpen, BarChart3, Target, Menu, FileText, Maximize2, Minimize2, Eye, EyeOff, CheckCircle, ChevronDown, ChevronUp, EyeIcon
} from 'lucide-react';
import { getImageUrl } from '../utils/questionHtml';
import { watchImageHeight } from '../utils/imageHeightLimit';
import QuestionHtmlWithZoom from './QuestionHtmlWithZoom';
import QuizQuestionTitle from './admin/QuizQuestionTitle';
import { getSupabaseUrl, supabase } from '../supabase/client';

interface Question {
  id: string;
  type: string;
  subType?: string;
  title?: string; // 题目名称（所有题型统一）
  content?: string; // 题干内容（富文本）
  options: { id: string; text: string; isCorrect?: boolean; image?: string; imageWidth?: number }[];
  score: number;
  titleImages?: string[];
  titleImageWidths?: number[];
  titleImageLayout?: string;
  imagePosition?: string;
  optionLayout?: string;
  explanation?: string;
}

interface Paper {
  id: string;
  title: string;
  total_score: number;
}

interface QuestionStats {
  questionId: string;
  accuracy: number;
  totalAnswers: number;
  correctAnswers: number;
}

interface PaperStats {
  highestScore: number;
  lowestScore: number;
  averageScore: number;
  totalStudents: number;
}

// 客观题答题数据
interface ObjectiveStudentAnswer {
  studentId: string;
  studentName: string;
  answer: string;
  isCorrect: boolean;
  score: number;
  hasAnswered: boolean;
}

// 非客观题答题数据
interface SubjectiveStudentAnswer {
  studentId: string;
  studentName: string;
  score: number;
  gradingStatus: 'teacher_graded' | 'final' | 'pending';
  statusLabel: string;
  recordId: string;
  hasAnswered: boolean;
}

// 联合类型
type StudentAnswer = ObjectiveStudentAnswer | SubjectiveStudentAnswer;

interface PaperRanking {
  rank: number;
  studentId: string;
  studentName: string;
  score: number;
  answerTime: number;
}

interface QuizTeacherViewProps {
  paper: Paper;
  questions: Question[];
  paperStats: PaperStats;
  questionStats: QuestionStats[];
  rankings: PaperRanking[];
  onBack: () => void;
  onViewStudentDetail?: (studentId: string) => void;
}

const typeNames: Record<string, string> = {
  single_choice: '单选题',
  multi_choice: '多选题',
  judge: '判断题',
  subjective: '主观题',
  python_coding: 'Python编程题',
  cpp_coding: 'C++编程题',
  scratch_coding: 'Scratch编程题',
  graphics: 'Scratch编程题'
};

const getAccuracyColor = (accuracy: number) => {
  if (accuracy >= 80) return 'text-green-600';
  if (accuracy >= 60) return 'text-amber-600';
  return 'text-red-600';
};

const getAccuracyBg = (accuracy: number) => {
  if (accuracy >= 80) return 'bg-green-50 border-green-200';
  if (accuracy >= 60) return 'bg-amber-50 border-amber-200';
  return 'bg-red-50 border-red-200';
};

// 获取阅卷状态显示
const getGradingStatusBadge = (status: string) => {
  switch (status) {
    case 'teacher_graded':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
          已人工阅卷
        </span>
      );
    case 'final':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
          已人工审核
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
          待阅卷
        </span>
      );
  }
};

export default function QuizTeacherView({
  paper,
  questions,
  paperStats,
  questionStats,
  rankings,
  onBack,
  onViewStudentDetail
}: QuizTeacherViewProps) {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswerMap, setShowAnswerMap] = useState<Record<string, boolean>>({});
  const [showRankingModal, setShowRankingModal] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // 答题情况模态框状态
  const [showAnswerDetailModal, setShowAnswerDetailModal] = useState(false);
  const [answerDetailLoading, setAnswerDetailLoading] = useState(false);
  const [answerDetailData, setAnswerDetailData] = useState<StudentAnswer[]>([]);
  const [answerDetailFilter, setAnswerDetailFilter] = useState<'all' | 'correct' | 'wrong' | 'unanswered' | 'teacher_graded' | 'final' | 'pending'>('all');
  const [answerDetailSort, setAnswerDetailSort] = useState<'name' | 'score'>('name');
  const [answerDetailSortOrder, setAnswerDetailSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isObjectiveQuestion, setIsObjectiveQuestion] = useState(true); // 是否为客观题

  // 选项图片容器 ref — 接入与富文本编辑器一致的图片高度限制
  // watchImageHeight 会自动遍历容器内所有 img，统一应用 maxHeight = 容器宽度 × 70% 规则
  const optionImagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = optionImagesContainerRef.current;
    if (!container) return;

    let cleanupFn: (() => void) | null = null;
    let cancelled = false;

    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      try {
        cleanupFn = watchImageHeight(container);
      } catch (error) {
        console.warn('Failed to initialize teacher view image height observer:', error);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (cleanupFn) cleanupFn();
    };
  }, [currentQuestion?.id, currentQuestion?.options]);

  const currentQuestion = questions[currentIndex];
  const currentStats = questionStats.find(qs => qs.questionId === currentQuestion?.id) || {
    questionId: currentQuestion?.id || '',
    accuracy: 0,
    totalAnswers: 0,
    correctAnswers: 0
  };

  const groupQuestionsByType = () => {
    const groups: { type: string; name: string; indices: number[] }[] = [];
    const typeOrder = ['single_choice', 'multi_choice', 'judge', 'scratch_coding', 'python_coding', 'cpp_coding', 'coding'];
    questions.forEach((q, idx) => {
      let groupType = q.type;
      if (q.type === 'coding' && q.subType) groupType = `${q.subType}_coding`;
      const existingGroup = groups.find(g => g.type === groupType);
      if (existingGroup) existingGroup.indices.push(idx);
      else groups.push({ type: groupType, name: typeNames[q.type] || '单选题', indices: [idx] });
    });
    return groups.sort((a, b) => {
      const indexA = typeOrder.indexOf(a.type);
      const indexB = typeOrder.indexOf(b.type);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });
  };

  const toggleAnswer = (questionId: string) => {
    setShowAnswerMap(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  // 判断是否为客观题
  const isObjectiveQuestionType = (type: string) => {
    return ['single_choice', 'multi_choice', 'judge'].includes(type);
  };

  // 从 Edge Function 获取答题情况
  const loadQuestionAnswers = async (questionId: string, questionType: string) => {
    setAnswerDetailLoading(true);
    try {
      const isObjective = isObjectiveQuestionType(questionType);
      setIsObjectiveQuestion(isObjective);
      // 重置筛选条件
      setAnswerDetailFilter('all');

      const session = await supabase.auth.getSession();
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/edu-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.data.session?.access_token}`,
        },
        body: JSON.stringify({ action: 'get_question_answers', paperId: paper.id, questionId, questionType }),
      });

      const result = await response.json();
      if (result.success) {
        setAnswerDetailData(result.data || []);
      } else {
        setAnswerDetailData([]);
      }
    } catch (error) {
      console.error('加载答题情况失败:', error);
      setAnswerDetailData([]);
    } finally {
      setAnswerDetailLoading(false);
    }
  };

  // 打开答题情况模态框
  const handleOpenAnswerDetail = async () => {
    if (!currentQuestion) return;
    await loadQuestionAnswers(currentQuestion.id, currentQuestion.type);
    setShowAnswerDetailModal(true);
  };

  // 跳转到阅卷详情页
  const handleViewGradingDetail = (recordId: string) => {
    const returnUrl = encodeURIComponent(`/teacher/paper-review/${paper.id}`);
    navigate(`/teacher/grading/${recordId}?returnUrl=${returnUrl}`);
  };

  // 筛选和排序答题数据
  const getFilteredAndSortedAnswers = () => {
    let filtered = [...answerDetailData];

    // 筛选
    if (isObjectiveQuestion) {
      // 客观题筛选
      if (answerDetailFilter === 'correct') {
        filtered = filtered.filter(a => (a as ObjectiveStudentAnswer).isCorrect);
      } else if (answerDetailFilter === 'wrong') {
        filtered = filtered.filter(a => !(a as ObjectiveStudentAnswer).isCorrect && (a as ObjectiveStudentAnswer).hasAnswered);
      } else if (answerDetailFilter === 'unanswered') {
        filtered = filtered.filter(a => !(a as ObjectiveStudentAnswer).hasAnswered);
      }
    } else {
      // 非客观题筛选
      if (answerDetailFilter === 'unanswered') {
        filtered = filtered.filter(a => !(a as SubjectiveStudentAnswer).hasAnswered);
      } else if (answerDetailFilter !== 'all') {
        filtered = filtered.filter(a => (a as SubjectiveStudentAnswer).gradingStatus === answerDetailFilter);
      }
    }

    // 排序
    filtered.sort((a, b) => {
      if (answerDetailSort === 'name') {
        return answerDetailSortOrder === 'asc'
          ? a.studentName.localeCompare(b.studentName)
          : b.studentName.localeCompare(a.studentName);
      } else {
        return answerDetailSortOrder === 'asc'
          ? a.score - b.score
          : b.score - a.score;
      }
    });

    return filtered;
  };

  const getQuestionStatus = (idx: number) => {
    const q = questions[idx];
    const stats = questionStats.find(s => s.questionId === q?.id);
    if (!stats || stats.totalAnswers === 0) return 'unanswered';
    if (stats.accuracy >= 80) return 'correct';
    if (stats.accuracy >= 60) return 'partial';
    return 'wrong';
  };

  // 渲染题目内容 - 完全复用 Quiz 页面的渲染逻辑
  const renderQuestionTitle = (question: Question) => {
    const hasTitleImages = question.titleImages && question.titleImages.length > 0 && !question.content?.includes('<img');
    const hasTitleContentImages = question.content?.includes('<img');
    const imagePosition = question.imagePosition || 'below';
    const currentLayout = question.titleImageLayout || 'multi';

    // 判断是否为编程题（非客观题）
    const isCodingQuestion = question.type === 'python_coding' ||
                             question.type === 'cpp_coding' ||
                             question.type === 'scratch_coding';

    // 非客观题显示题目名称：有 title 则按此内容显示，否则显示"未命名+序号"
    const questionName = isCodingQuestion
      ? (question.title ?? (() => {
          const codingQuestions = questions.filter(q =>
            q.type === 'python_coding' || q.type === 'cpp_coding' || q.type === 'scratch_coding'
          );
          const codingIndex = codingQuestions.findIndex(q => q.id === question.id) + 1;
          return `未命名${codingIndex}`;
        })())
      : null;

    const questionStem = question.content || '';
    const titleText = questionStem?.includes('<img') || questionStem?.startsWith('<') ? (
      <div className="prose prose-sm max-w-none">
        <QuestionHtmlWithZoom
          html={questionStem}
          imageWidths={question.titleImages?.map(i => i.widthPercent) || question.titleImageWidths}
        />
      </div>
    ) : (
      <span>{questionStem}</span>
    );

    const titleImagesEl = hasTitleImages ? (
      (() => {
    // 自动布局：根据图片数量决定单行还是多行
    const imageCount = question.titleImages?.length || 1;
    const gridCols = imageCount <= 2 ? imageCount : 2;
        return (
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}>
            {question.titleImages?.map((imgItem, idx) => {
              // 兼容新旧格式：ImageItem 对象或字符串
              const imgUrl = typeof imgItem === 'string' ? imgItem : imgItem?.url;
              const fullUrl = getImageUrl(imgUrl);
              return (
                <div key={idx} className="relative overflow-hidden">
                  <img
                    src={fullUrl}
                    alt={`题目图片 ${idx + 1}`}
                    className="rounded-lg border border-gray-200"
                    style={{ width: '100%', maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                    loading="lazy"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
              );
            })}
          </div>
        );
      })()
    ) : null;

    return (
      <div className="relative">
        {/* 编程题始终显示题目名称：有内容按内容显示，无内容显示"未命名+序号" */}
        {isCodingQuestion && (
          <div className="mb-3 pb-3 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-500">题目名称：</span>
            <span className="text-base font-semibold text-gray-800">{questionName}</span>
          </div>
        )}
        {imagePosition === 'above' && titleImagesEl}
        {titleText}
        {imagePosition === 'below' && titleImagesEl}
      </div>
    );
  };

  // 渲染选项 - 完全复用 Quiz 页面的渲染逻辑
  const renderOptions = (question: Question) => {
    const showAnswer = showAnswerMap[question.id] || false;
    const isCodingQuestion = question.type === 'scratch_coding' || 
                            question.type === 'python_coding' || 
                            question.type === 'cpp_coding';

    if (isCodingQuestion) {
      return renderCodingQuestion(question);
    }

    return (
      <div className={`grid gap-3 ${(question.optionLayout || 'grid') === 'grid' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
        {question.options?.map((option) => {
          const isCorrect = option.isCorrect;
          const showCorrect = showAnswer && isCorrect;

          return (
            <div
              key={option.id}
              className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-200 flex items-start gap-3 ${
                showCorrect
                  ? 'border-green-500 bg-green-50 shadow-sm'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <span className={`flex-shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-medium transition-all duration-200 ${
                showCorrect
                  ? 'border-green-500 bg-green-500 text-white'
                  : 'border-gray-300 text-gray-500 bg-white'
              }`}>
                {option.id}
              </span>
              <div className="flex-1 text-gray-800">
                {option.text?.startsWith('<') ? (
                  <div dangerouslySetInnerHTML={{ __html: option.text }} />
                ) : (
                  <span className="whitespace-pre-line">{option.text}</span>
                )}
                {/* 处理选项图片(支持多张) */}
                {(() => {
                  const optionImages = option.images && option.images.length > 0
                    ? option.images
                    : (option.image ? [option.image] : []);
                  const optionImageWidths = option.imageWidths && option.imageWidths.length > 0
                    ? option.imageWidths
                    : (option.imageWidth ? [option.imageWidth] : []);

                  if (optionImages.length === 0) return null;

                  return (
                    <div className="mt-2 grid grid-cols-2 gap-3">
                      {optionImages.map((imgUrl: string, imgIdx: number) => {
                        const displayWidth = optionImageWidths[imgIdx] ?? 100;
                        return (
                          <div key={imgIdx}>
                            <img
                              src={imgUrl.startsWith('/sb-api/') ? imgUrl : imgUrl.startsWith('/storage/') ? `${getSupabaseUrl()}${imgUrl}` : imgUrl}
                              alt={`选项${option.id}图片${imgIdx + 1}`}
                              className="rounded-lg border border-gray-200"
                              style={{ width: `${displayWidth}%`, height: 'auto', objectFit: 'contain' }}
                              loading="lazy"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
              {showCorrect && <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />}
            </div>
          );
        })}
      </div>
    );
  };

  // 渲染编程题 - 教师模式下只显示题目内容，不显示【你的答案】和【评分结果】
  const renderCodingQuestion = (question: Question) => {
    // 教师模式下不渲染编程题结果组件（这些组件包含【你的答案】和【评分结果】）
    // 只显示题目说明文字
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-600 text-sm">编程题：请查看答题情况了解学生作答详情</p>
      </div>
    );
  };

  const groupedQuestions = groupQuestionsByType();
  const typeLabels = ['一', '二', '三', '四', '五'];

  // 客观题筛选按钮
  const objectiveFilters = [
    { key: 'all', label: '全部' },
    { key: 'correct', label: '正确' },
    { key: 'wrong', label: '错误' },
    { key: 'unanswered', label: '未作答' }
  ];

  // 非客观题筛选按钮
  const subjectiveFilters = [
    { key: 'all', label: '全部' },
    { key: 'teacher_graded', label: '已人工阅卷' },
    { key: 'final', label: '已人工审核' },
    { key: 'pending', label: '待阅卷' },
    { key: 'unanswered', label: '未作答' }
  ];

  return (
    <div className={isFullscreen ? "fixed inset-0 z-50 bg-gray-50 overflow-auto" : "min-h-screen bg-gray-50"}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className={isFullscreen ? "max-w-full mx-auto px-6 h-16 flex items-center justify-between" : "max-w-7xl mx-auto px-4 h-16 flex items-center justify-between"}>
          <div className="flex items-center gap-3">
            {!isFullscreen && (
              <button
                onClick={onBack}
                className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="text-sm">返回</span>
              </button>
            )}
          </div>
          <div className="flex flex-col items-center">
            <h1 className="font-bold text-gray-900 text-lg">{paper.title}</h1>
            <span className="text-xs text-gray-500">评讲模式</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {isFullscreen ? (
                <><Minimize2 className="w-4 h-4" />退出全屏</>
              ) : (
                <><Maximize2 className="w-4 h-4" />全屏</>
              )}
            </button>
          </div>
        </div>
      </header>

      <div className={isFullscreen ? "max-w-full mx-auto px-6 py-6" : "max-w-7xl mx-auto px-4 py-6"}>
        <div className={isFullscreen ? "grid grid-cols-12 gap-6" : "grid grid-cols-1 lg:grid-cols-4 gap-6"}>
          {/* Sidebar - Answer Card */}
          <div className={isFullscreen ? "hidden lg:block lg:sticky lg:top-20 lg:self-start col-span-2" : "hidden lg:block lg:sticky lg:top-20 lg:self-start"}>
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 border-b border-purple-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-purple-800 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    答题卡
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  共 <span className="font-bold text-purple-600">{questions.length}</span> 题
                </div>
              </div>
              <div className="p-4 max-h-[50vh] overflow-y-auto">
                {groupedQuestions.map((group, groupIdx) => (
                  <div key={group.type} className="mb-4">
                    <div className="text-sm font-bold text-gray-800 mb-2">
                      {typeLabels[groupIdx]}、{group.name}（{group.indices.length}题）
                    </div>
                    <div className="grid grid-cols-5 gap-2">
                      {group.indices.map((idx) => {
                        const isCurrent = idx === currentIndex;
                        const status = getQuestionStatus(idx);
                        return (
                          <button
                            key={idx}
                            onClick={() => setCurrentIndex(idx)}
                            className={`aspect-square w-full text-sm font-medium rounded-lg border-2 transition-all flex items-center justify-center ${
                              isCurrent
                                ? 'border-purple-500 bg-purple-500 text-white'
                                : status === 'correct'
                                ? 'bg-green-500 text-white border-green-500'
                                : status === 'partial'
                                ? 'bg-amber-500 text-white border-amber-500'
                                : status === 'wrong'
                                ? 'bg-red-500 text-white border-red-500'
                                : 'bg-white text-gray-900 border-gray-300 hover:bg-purple-50'
                            }`}
                          >
                            {String(idx + 1).padStart(2, '0')}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Stats Section - 本卷统计 */}
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-purple-600" />
                  本卷统计
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">试卷总分</span>
                    <span className="font-medium text-gray-800">{paper?.total_score || 0}分</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">最高分</span>
                    <span className="font-medium text-green-600">{paperStats.highestScore}分</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">最低分</span>
                    <span className="font-medium text-red-600">{paperStats.lowestScore}分</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">平均分</span>
                    <span className="font-medium text-purple-600">{paperStats.averageScore}分</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">答题人数</span>
                    <span className="font-medium text-gray-800">{paperStats.totalStudents}人</span>
                  </div>
                </div>
                <button
                  onClick={() => setShowRankingModal(true)}
                  className="w-full mt-3 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                >
                  <Award className="w-4 h-4" />
                  查看排名
                </button>

                {/* 本题情况 */}
                {currentQuestion && currentStats && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <Target className="w-4 h-4 text-purple-600" />
                      本题情况
                    </h3>
                    <div className="space-y-2">
                      {/* 正确率 */}
                      <div className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border ${getAccuracyBg(currentStats.accuracy)}`}>
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          正确率：{currentStats.accuracy}%（{currentStats.correctAnswers}/{currentStats.totalAnswers}人）
                        </span>
                      </div>

                      {/* 查看答题情况按钮 */}
                      <button
                        onClick={handleOpenAnswerDetail}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors bg-purple-100 text-purple-700 hover:bg-purple-200"
                      >
                        <Users className="w-4 h-4" />
                        查看答题情况
                      </button>

                      {/* 查看答案按钮 */}
                      <button
                        onClick={() => toggleAnswer(currentQuestion.id)}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                      >
                        {showAnswerMap[currentQuestion.id] ? (
                          <><EyeOff className="w-4 h-4" />隐藏答案</>
                        ) : (
                          <><Eye className="w-4 h-4" />查看答案</>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content - Question Display */}
          <div className={isFullscreen ? "col-span-10 space-y-4" : "lg:col-span-3 space-y-4"}>
            {/* Question Header with Navigation */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="sticky lg:top-20 z-30 bg-gradient-to-r from-purple-50 to-purple-100 p-6 border-b border-purple-200">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="px-3 py-1 bg-purple-600 text-white text-sm font-medium rounded-full">
                      {typeNames[currentQuestion?.type] || '单选题'}
                    </span>
                    <span className="text-sm text-gray-600">
                      第 {currentIndex + 1} / {questions.length} 题
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                      disabled={currentIndex === 0}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 text-sm font-medium transition-all"
                    >
                      <ChevronLeft className="w-4 h-4" />上一题
                    </button>
                    <button
                      onClick={() => setCurrentIndex(Math.min(questions.length - 1, currentIndex + 1))}
                      disabled={currentIndex === questions.length - 1}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 text-sm font-medium transition-all"
                    >
                      下一题<ChevronLeft className="w-4 h-4 rotate-180" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Question Content */}
              <div className="p-6">
                {currentQuestion && (
                  <div className="space-y-6">
                    <QuizQuestionTitle
                      question={currentQuestion}
                      currentIndex={currentIndex}
                      examQuestions={questions}
                      isPreview={false}
                      showCodingTitle={true}
                      sizeVariant='compact'
                      numberColor='text-purple-600'
                    />

                    {/* 选项或编程题 */}
                    <div ref={optionImagesContainerRef} className="mt-6">
                      {renderOptions(currentQuestion)}
                    </div>

                    {/* 答案展示区域 */}
                    {showAnswerMap[currentQuestion.id] && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mt-6 bg-gray-50 rounded-lg border border-gray-200 p-4 overflow-hidden"
                      >
                        <div className="space-y-3 text-sm">
                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-600">正确答案：</span>
                              <span className="font-medium text-green-600">
                                {currentQuestion.options?.filter(o => o.isCorrect).map(o => o.id).join(', ') || '-'}
                              </span>
                            </div>
                          </div>
                          {currentQuestion.explanation && (
                            <div className="pt-3 border-t border-gray-200">
                              <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                                <BookOpen className="w-4 h-4 text-purple-600" />答案解析
                              </h4>
                              <div className="text-gray-700 leading-relaxed">{currentQuestion.explanation}</div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Toggle */}
      <button
        onClick={() => setShowSidebar(true)}
        className="lg:hidden fixed bottom-4 right-4 w-12 h-12 bg-purple-600 text-white rounded-full shadow-lg flex items-center justify-center z-40"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {showSidebar && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-y-0 right-0 w-80 bg-white shadow-xl z-50 lg:hidden"
          >
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-bold text-gray-900">答题卡</h2>
              <button onClick={() => setShowSidebar(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {groupedQuestions.map((group, groupIdx) => (
                <div key={group.type} className="mb-4">
                  <div className="text-sm font-bold text-gray-800 mb-2">
                    {typeLabels[groupIdx]}、{group.name}
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {group.indices.map((idx) => (
                      <button
                        key={idx}
                        onClick={() => { setCurrentIndex(idx); setShowSidebar(false); }}
                        className={`aspect-square w-full text-sm font-medium rounded-lg border-2 transition-all flex items-center justify-center ${
                          idx === currentIndex
                            ? 'border-purple-500 bg-purple-500 text-white'
                            : 'bg-white text-gray-900 border-gray-300'
                        }`}
                      >
                        {String(idx + 1).padStart(2, '0')}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Ranking Modal */}
      <AnimatePresence>
        {showRankingModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowRankingModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-lg max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-600" />
                  试卷排名
                </h3>
                <button
                  onClick={() => setShowRankingModal(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <div className="overflow-auto max-h-[60vh] p-4">
                {rankings.length > 0 ? (
                  <div className="space-y-2">
                    {rankings.map((rank) => (
                      <div
                        key={rank.studentId}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            rank.rank === 1 ? 'bg-amber-100 text-amber-600' :
                            rank.rank === 2 ? 'bg-gray-100 text-gray-600' :
                            rank.rank === 3 ? 'bg-orange-100 text-orange-600' :
                            'bg-gray-200 text-gray-600'
                          }`}>
                            {rank.rank}
                          </span>
                          <span className="font-medium text-gray-800">{rank.studentName}</span>
                        </div>
                        <span className="font-bold text-purple-600">{rank.score}分</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-400 py-8">暂无排名数据</div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Answer Detail Modal - 答题情况模态框 */}
      <AnimatePresence>
        {showAnswerDetailModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAnswerDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-purple-100">
                <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  答题情况
                  <span className="text-sm text-gray-500 font-normal">
                    （第{currentIndex + 1}题）
                  </span>
                </h3>
                <button
                  onClick={() => setShowAnswerDetailModal(false)}
                  className="p-1 hover:bg-white/50 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Filter & Sort Bar */}
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center gap-3">
                {/* 筛选 */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">筛选：</span>
                  <div className="flex gap-1">
                    {(isObjectiveQuestion ? objectiveFilters : subjectiveFilters).map(({ key, label }) => (
                      <button
                        key={key}
                        onClick={() => setAnswerDetailFilter(key as typeof answerDetailFilter)}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          answerDetailFilter === key
                            ? 'bg-purple-600 text-white'
                            : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 排序 */}
                <div className="flex items-center gap-2 ml-auto">
                  <span className="text-sm text-gray-600">排序：</span>
                  <select
                    value={answerDetailSort}
                    onChange={(e) => setAnswerDetailSort(e.target.value as 'name' | 'score')}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none"
                  >
                    <option value="name">姓名</option>
                    <option value="score">得分</option>
                  </select>
                  <button
                    onClick={() => setAnswerDetailSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                    className="p-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    title={answerDetailSortOrder === 'asc' ? '升序' : '降序'}
                  >
                    {answerDetailSortOrder === 'asc' ? (
                      <ChevronUp className="w-4 h-4 text-gray-600" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-600" />
                    )}
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="overflow-auto max-h-[60vh]">
                {answerDetailLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    <span className="ml-3 text-gray-600">加载中...</span>
                  </div>
                ) : getFilteredAndSortedAnswers().length > 0 ? (
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">序号</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">学员姓名</th>
                        {isObjectiveQuestion ? (
                          <>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">所选答案</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">是否正确</th>
                          </>
                        ) : (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">阅卷状态</th>
                        )}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">得分</th>
                        {!isObjectiveQuestion && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">操作</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {getFilteredAndSortedAnswers().map((answer, index) => (
                        <motion.tr
                          key={answer.studentId}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.03 }}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-800">{answer.studentName}</td>
                          {isObjectiveQuestion ? (
                            <>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {(answer as ObjectiveStudentAnswer).hasAnswered
                                  ? (answer as ObjectiveStudentAnswer).answer || '-'
                                  : <span className="text-gray-400">未作答</span>
                                }
                              </td>
                              <td className="px-4 py-3">
                                {(answer as ObjectiveStudentAnswer).hasAnswered ? (
                                  (answer as ObjectiveStudentAnswer).isCorrect ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                                      <CheckCircle className="w-3 h-3" />正确
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                                      <X className="w-3 h-3" />错误
                                    </span>
                                  )
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs font-medium">
                                    -
                                  </span>
                                )}
                              </td>
                            </>
                          ) : (
                            <td className="px-4 py-3">
                              {(answer as SubjectiveStudentAnswer).hasAnswered
                                ? getGradingStatusBadge((answer as SubjectiveStudentAnswer).gradingStatus)
                                : <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs font-medium">未作答</span>
                              }
                            </td>
                          )}
                          <td className="px-4 py-3 text-sm font-medium text-gray-800">{answer.score}分</td>
                          {!isObjectiveQuestion && (
                            <td className="px-4 py-3">
                              {(answer as SubjectiveStudentAnswer).hasAnswered ? (
                                <button
                                  onClick={() => handleViewGradingDetail((answer as SubjectiveStudentAnswer).recordId)}
                                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                >
                                  <EyeIcon className="w-4 h-4" />
                                  查看
                                </button>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </td>
                          )}
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">暂无答题数据</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  共 <span className="font-medium text-gray-800">{getFilteredAndSortedAnswers().length}</span> 人
                  {answerDetailFilter !== 'all' && (
                    <span className="text-gray-500">（已筛选）</span>
                  )}
                </span>
                <button
                  onClick={() => setShowAnswerDetailModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                >
                  关闭
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
